/**
 * Run history + one run's detail.
 *
 * Shared on purpose, twice over. The Schedulers screen, a General Process's
 * "Schedules & runs" tab and a Report's Renders list are the SAME rows from
 * `twasta_job_runs` filtered differently; and the platform Run view and a
 * downloaded runtime render the same component over different adapters.
 * Separate implementations would be separate chances to disagree about what
 * "skipped" means.
 */
import { useCallback, useEffect, useState } from 'react'
import type { JobRun, RunStatus, SchedulerRuntime } from './contract'
import {
  C, Chip, Empty, StatusChip, cardStyle, formatDuration, formatWhen, labelStyle,
} from './ui'

const FILTERS: { key: string; label: string; status?: RunStatus; trigger?: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'scheduled', label: 'Scheduled', trigger: 'schedule' },
  { key: 'manual', label: 'Manual', trigger: 'manual' },
  { key: 'failed', label: 'Failed', status: 'failed' },
]

export default function RunsPanel({
  runtime, scheduleName, objName, title = 'Run history', pollMs = 0,
  compact = false,
}: {
  runtime: SchedulerRuntime
  /** Filter to one scheduler's runs. */
  scheduleName?: string
  /** Filter to one object's runs, however they started. */
  objName?: string
  title?: string
  /** Poll while something is running; 0 disables. */
  pollMs?: number
  compact?: boolean
}) {
  const [runs, setRuns] = useState<JobRun[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('all')
  const [selected, setSelected] = useState<JobRun | null>(null)

  const reload = useCallback(async () => {
    const f = FILTERS.find((x) => x.key === filter)
    try {
      const rows = await runtime.listRuns({
        name: scheduleName, objName,
        status: f?.status, trigger: f?.trigger, limit: 60,
      })
      setRuns(rows || [])
      setError(null)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setRuns([])
      setError(msg)
    }
  }, [runtime, scheduleName, objName, filter])

  useEffect(() => { void reload() }, [reload])

  // Poll only while something is actually moving — a finished history does not
  // change, and a screen that refetches every few seconds for nothing is just
  // load with a spinner on it.
  const busy = (runs || []).some((r) => r.status === 'running' || r.status === 'queued')
  useEffect(() => {
    if (!pollMs || !busy) return undefined
    const id = window.setInterval(() => { void reload() }, pollMs)
    return () => window.clearInterval(id)
  }, [pollMs, busy, reload])

  const openRun = useCallback(async (run: JobRun) => {
    setSelected(run)
    try {
      setSelected(await runtime.getRun(run.run_id))
    } catch {
      /* keep the header we already have — better than an empty pane */
    }
  }, [runtime])

  return (
    <div style={{ display: 'grid',
      gridTemplateColumns: compact ? '1fr' : 'minmax(0,1fr) 340px',
      gap: 14, minHeight: 0 }}>
      <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderBottom: `1px solid ${C.line}` }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>{title}</div>
          <div style={{ display: 'inline-flex', padding: 2, background: C.s2,
            border: `1px solid ${C.line}`, borderRadius: 999 }}>
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                style={{
                  padding: '3px 10px', borderRadius: 999, border: 'none',
                  cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                  background: filter === f.key ? C.panel : 'transparent',
                  color: filter === f.key ? C.accent : C.ink2,
                }}>
                {f.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => void reload()} title="Refresh"
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              color: C.ink2, display: 'flex' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" /></svg>
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {error && (
            <div style={{ padding: '12px 14px', color: C.err, fontSize: '0.8825rem' }}>
              {error}
            </div>
          )}
          {runs === null && !error && (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.ink2,
              fontSize: '0.8825rem' }}>Loading…</div>
          )}
          {runs !== null && runs.length === 0 && !error && (
            <Empty title="Nothing has run yet"
              hint={scheduleName
                ? 'Press Run now to try it without waiting for the cadence.'
                : 'Runs appear here whether they started from a scheduler, from a person, or from an inbound call.'} />
          )}
          {runs !== null && runs.length > 0 && (
            <table style={{ borderCollapse: 'collapse', width: '100%',
              fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Started', 'Trigger', 'Took', 'Status', 'Result'].map((h) => (
                    <th key={h} style={{
                      textAlign: 'left', ...labelStyle, padding: '7px 14px',
                      borderBottom: `1px solid ${C.line}`, background: C.s2,
                      position: 'sticky', top: 0,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.run_id} onClick={() => void openRun(run)}
                    style={{
                      cursor: 'pointer',
                      background: selected?.run_id === run.run_id
                        ? C.accentWeak : 'transparent',
                    }}>
                    <td style={cellStyle(true)}>{formatWhen(run.queued_at)}</td>
                    <td style={cellStyle()}>{triggerText(run)}</td>
                    <td style={cellStyle()}>{formatDuration(run.duration_seconds)}</td>
                    <td style={cellStyle()}><StatusChip status={run.status} /></td>
                    <td style={cellStyle()}>{resultText(run, runtime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {!compact && <RunDetail runtime={runtime} run={selected} />}
    </div>
  )
}

function cellStyle(strong = false) {
  return {
    padding: '8px 14px', borderBottom: `1px solid ${C.line}`,
    color: strong ? C.ink : C.ink2, fontWeight: strong ? 500 : 400,
    verticalAlign: 'middle' as const,
  }
}

function triggerText(run: JobRun) {
  const who = run.actor ? ` · ${run.actor}` : ''
  if (run.trigger_kind === 'schedule') return run.schedule_name || 'Schedule'
  if (run.trigger_kind === 'manual') return `Manual${who}`
  if (run.trigger_kind === 'api') return 'Inbound API'
  return run.trigger_kind
}

function resultText(run: JobRun, runtime: SchedulerRuntime) {
  if (run.status === 'failed') {
    return <span style={{ color: C.err }}>{run.result_summary || run.error_text || 'Failed'}</span>
  }
  const ref = run.output_ref
  if (ref?.filename) {
    return (
      <a href={runtime.outputUrl(run.run_id, ref.filename)}
        onClick={(e) => e.stopPropagation()}
        style={{ color: C.accent, textDecoration: 'none' }}>
        {ref.filename}
      </a>
    )
  }
  return run.result_summary || '—'
}

function RunDetail({ runtime, run }: { runtime: SchedulerRuntime; run: JobRun | null }) {
  if (!run) {
    return (
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 24 }}>
        <span style={{ color: C.ink2, fontSize: '0.8825rem', textAlign: 'center' }}>
          Pick a run to see its steps.
        </span>
      </div>
    )
  }
  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column',
      minHeight: 0, overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', borderBottom: `1px solid ${C.line}` }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>
          Run <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
            {run.run_id.slice(0, 8)}
          </span>
        </div>
        <StatusChip status={run.status} />
      </div>

      <div style={{ padding: '11px 14px', borderBottom: `1px solid ${C.line}`,
        fontSize: '0.8425rem', color: C.ink2 }}>
        <div>{formatWhen(run.started_at || run.queued_at)}
          {run.finished_at ? ` → ${formatWhen(run.finished_at)}` : ''}</div>
        <div style={{ marginTop: 2 }}>{triggerText(run)}</div>
      </div>

      {(run.steps || []).map((step) => (
        <div key={step.run_id} style={{ display: 'flex', gap: 9,
          padding: '10px 14px', borderBottom: `1px solid ${C.line}`,
          fontSize: '0.8625rem' }}>
          <StepIcon status={step.status} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, color: C.ink }}>
              {step.step_no} · {step.obj_name || step.kind}
            </div>
            <div style={{ color: C.ink2, fontSize: '0.8225rem' }}>
              {step.kind}
              {step.duration_seconds != null && ` · ${formatDuration(step.duration_seconds)}`}
              {step.result_summary ? ` · ${step.result_summary}` : ''}
            </div>
            {step.error_text && (
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.8125rem',
                background: C.errWeak, border: `1px solid ${C.err}33`,
                borderRadius: 6, padding: '8px 10px', marginTop: 7,
                color: C.err, whiteSpace: 'pre-wrap',
              }}>{step.error_text}</div>
            )}
            {step.output_ref?.filename && (
              <div style={{ marginTop: 6 }}>
                <a href={runtime.outputUrl(run.run_id, step.output_ref.filename)}
                  style={{ color: C.accent, fontSize: '0.8225rem', textDecoration: 'none' }}>
                  {step.output_ref.filename}
                </a>
                {step.output_ref.folder && (
                  <span style={{ color: C.faint, fontSize: '0.8125rem' }}>
                    {' '}→ {step.output_ref.repo}/{step.output_ref.folder}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {run.ioflow_execution_id && (
        <div style={{ padding: '11px 14px' }}>
          <div style={labelStyle}>Cross reference</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem',
            color: C.ink2, marginTop: 5 }}>
            IOFlow execution {run.ioflow_execution_id}
          </div>
        </div>
      )}
      {run.status === 'failed' && !run.steps?.length && run.error_text && (
        <div style={{ padding: '11px 14px' }}>
          <Chip tone="err">Failed before any step ran</Chip>
          <div style={{ marginTop: 7, fontSize: '0.8425rem', color: C.err }}>
            {run.error_text}
          </div>
        </div>
      )}
    </div>
  )
}

/** Succeeded / failed / skipped, drawn rather than set in an icon font — a
 *  downloaded runtime does not load Material Icons. */
function StepIcon({ status }: { status: RunStatus }) {
  const color = status === 'succeeded' ? C.ok : status === 'failed' ? C.err : C.faint
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.4" strokeLinecap="round"
      style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="12" cy="12" r="9" />
      {status === 'succeeded' && <path d="m8.5 12.2 2.4 2.4 4.6-4.8" />}
      {status === 'failed' && <path d="M15 9l-6 6M9 9l6 6" />}
      {status !== 'succeeded' && status !== 'failed' && <path d="M8 12h8" />}
    </svg>
  )
}
